import styled from 'styled-components';

// Page wrapper with themed background
export const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background || '#0f172a'
      : 'transparent'};
`;

// Container matching the layout structure
export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;
  box-sizing: border-box;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    padding: 16px;
    max-width: 100%;
    width: 100%;
    overflow-x: hidden;
  }
`;

// Dashboard Layout with sidebar
export const DashboardLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 24px;
  align-items: start;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    display: block;
    width: 100%;
  }
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const Sidebar = styled.div`
  position: sticky;
  top: 20px;

  @media (max-width: 1200px) {
    position: static;
    width: 100%;
    margin-top: 16px;
  }

  @media (max-width: 768px) {
    position: static;
    width: 100%;
    margin-top: 16px;
  }
`;

// Header Container
export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    align-items: center;
    gap: 12px;
  }
`;

export const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

export const HeaderSubtitle = styled.p`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.light || '#94a3b8'
      : props.theme.colors?.textGray?.dark || '#64748b'};
  margin: 4px 0 0 0;
`;

// Content wrapper for the table card
export const ContentWrapper = styled.div`
  padding: 24px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Page Header with search and filters
export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

// Customer name styled to match primary color
export const CustomerName = styled.span`
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

// Purchase amount styled
export const PurchaseAmount = styled.span`
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

// Date styling
export const PurchaseDate = styled.span`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : '#1e293b'};
  font-size: 13px;
  text-transform: uppercase;
`;

// Actions header section
export const ActionsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
    margin-top: 12px;
  }
`;

// Back button
export const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 12px;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)'};
  }
`;

// Export icons container
export const ExportIconsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  padding: 24px;
`;

// Export icon button
export const ExportIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};

  &:hover {
    transform: scale(1.05);
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 64px;
    height: 64px;
  }
`;

export const WaiverBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  ${(props) => {
    if (props.$signed) {
      return `
        background: ${
          props.theme.name === 'dark'
            ? 'rgba(34, 197, 94, 0.15)'
            : 'rgba(34, 197, 94, 0.1)'
        };
        color: ${props.theme.name === 'dark' ? '#4ade80' : '#16a34a'};
      `;
    }
    return `
      background: ${
        props.theme.name === 'dark'
          ? 'rgba(239, 68, 68, 0.15)'
          : 'rgba(239, 68, 68, 0.1)'
      };
      color: ${props.theme.name === 'dark' ? '#f87171' : '#dc2626'};
    `;
  }}
`;
